/*
 * COPYRIGHT Transtron Inc 2022
 *
 * 機能名：マスタ系のAPI
 *
 * 作成者：2021/11/19 ノア）黄
 * 最終修正者：2021/11/19 ノア）黄
 *
 */
import { apiClient } from '@/store/apis/apiClient'
import config from '@/config/config'
import {
  MasterData,
  MasterGetParam,
  MasterUpdateParam,
  GeneralData,
  GeneralRequestParam,
  CorporateMasterData,
  GpsTrackerTerminalConfigData,
  LockParam,
  CheckLockResponse,
  UpdateCorporateParam,
  PrefectureAllocData,
  ManagementTargetAllocData,
  TemplateDownloadParam,
  FileDownloadResponse,
  OfficeMasterData,
  TrustedRepliesCount,
  ImageGettingParam,
  TrackerExchangeForm,
  TrackerDetail,
  TrackerDetailParam
} from '@/models'

/**
 * 汎用定義を取得する。
 *
 * @param {GeneralRequestParam} codeTypeList 汎用定義タイプリスト
 * @returns {Promise<GeneralData>} 汎用定義データ
 */
export async function getGeneralData(codeTypeList: GeneralRequestParam): Promise<GeneralData> {
  const res = await apiClient.post(config.api.getGeneralData, codeTypeList)

  return res.data.data as GeneralData
}

/**
 * ロックチェック状況を取得する。
 *
 * @param {LockParam} checkLockParam 画面ロックパラメータ
 * @returns {Promise<CheckLockResponse>} ロックチェック
 */
export async function checklock(checkLockParam: LockParam): Promise<CheckLockResponse> {
  const res = await apiClient.post(config.api.checklock, checkLockParam)

  return res.data.data as CheckLockResponse
}

/**
 * ロックする。
 *
 * @param {LockParam} lockParam 画面ロックパラメータ
 * @returns {Promise<void>}
 */
export async function lock(lockParam: LockParam): Promise<void> {
  const res = await apiClient.post(config.api.lock, lockParam)
  if (!res || !res.data) {
    throw new Error('apis > lock: res is null')
  }
}

/**
 * ロックを取消する
 *
 * @param {LockParam} lockParam 画面ロックパラメータ
 * @returns {Promise<void>}
 */
export async function unlock(lockParam: LockParam): Promise<void> {
  const res = await apiClient.post(config.api.unlock, lockParam)
  if (!res || !res.data) {
    throw new Error('apis > lock: res is null')
  }
}

/**
 * テンプレートダウンロード
 *
 * @param {TemplateDownloadParam} templateDownloadParam テンプレートダウンロードパラメータ
 * @returns {Promise<FileDownloadResponse>} ダウンロードファイル
 */
export async function templateDownload(templateDownloadParam: TemplateDownloadParam): Promise<FileDownloadResponse> {
  const res = await apiClient.get(config.api.templateDownload, {
    params: templateDownloadParam,
    responseType: 'blob'
  })

  return res as unknown as FileDownloadResponse
}

/**
 * 会社情報を取得する。
 *
 * @async
 * @returns {Promise<CorporateMasterData>} 会社情報
 */
export async function getCorporateMasterData(): Promise<CorporateMasterData> {
  const res = await apiClient.get(config.api.getCorporateMasterData)

  return res.data.data as CorporateMasterData
}

/**
 * 会社情報を保存する。
 *
 * @async
 * @param {UpdateCorporateParam} params 会社情報保存パラメータ
 * @returns {Promise<void>}
 */
export async function updateCorporateMasterData(params: UpdateCorporateParam): Promise<void> {
  const res = await apiClient.post(config.api.updateCorporateMasterData, params)
  if (!res || !res.data) {
    throw new Error('apis > lock: res is null')
  }
}

/**
 * 営業所マスタを取得する。
 *
 * @async
 * @param {MasterGetParam} reqParams パラメータ
 * @returns {Promise<MasterData>} 営業所マスタ
 */
export async function getBusinessOfficeMasterData(reqParams?: MasterGetParam): Promise<MasterData> {
  const res = await apiClient.get(config.api.getBusinessOfficeMasterData, {
    params: reqParams
  })

  return res.data.data as MasterData
}

/**
 * 営業所マスタを更新する。
 *
 * @async
 * @param {MasterUpdateParam} businessOfficeMasterUpdateParam 営業所マスタ更新のパラメータ
 * @returns {Promise<void>}
 */
export async function updateBusinessOfficeMasterData(
  businessOfficeMasterUpdateParam: MasterUpdateParam
): Promise<void> {
  await apiClient.post(config.api.updateBusinessOfficeMaster, businessOfficeMasterUpdateParam)
}

/**
 * 営業所一覧CSVダウンロード
 *
 * @returns {Promise<FileDownloadResponse>} ダウンロードファイル
 */
export async function businessOfficeMasterCSVDownload(): Promise<FileDownloadResponse> {
  const res = await apiClient.get(config.api.businessOfficeMasterDownload, { responseType: 'blob' })

  return res as unknown as FileDownloadResponse
}

/**
 * ユーザマスタを取得する。
 *
 * @async
 * @returns {Promise<MasterData>} ユーザマスタ
 */
export async function getUserMasterData(): Promise<MasterData> {
  const res = await apiClient.get(config.api.getUserMasterData)

  return res.data.data as MasterData
}

/**
 *  ユーザマスタを更新する
 *
 * @async
 * @param {MasterUpdateParam} userMasterUpdateParam  ユーザマスタ更新のパラメータ
 * @returns {Promise<void>}
 */
export async function updateUserMasterData(userMasterUpdateParam: MasterUpdateParam): Promise<void> {
  await apiClient.post(config.api.updateUserMasterData, userMasterUpdateParam)
}

/**
 * エリア割当を取得する。
 *
 * @async
 * @returns {Promise<PrefectureAllocData>} エリア割当
 */
export async function getAreaAllocationMasterData(): Promise<PrefectureAllocData> {
  const res = await apiClient.get(config.api.getAreaAllocationMasterData)

  return res.data.data as PrefectureAllocData
}

/**
 * 下位都道府県ありエリア一覧取得。
 *
 * @async
 * @returns {Promise<PrefectureAllocData>} エリア割当
 */
export async function getAreaMasterDataWhoHasSubPrefecture(): Promise<PrefectureAllocData> {
  const res = await apiClient.get(config.api.getAreaMasterDataWhoHasSubPrefecture)

  return res.data.data as PrefectureAllocData
}

/**
 * 都道府県割当保存
 *
 * @async
 * @param {MasterUpdateParam} updateParam  ユーザマスタ更新のパラメータ
 * @returns {Promise<void>}
 */
export async function updateAreaAllocationMasterData(updateParam: MasterUpdateParam): Promise<void> {
  await apiClient.post(config.api.updateAreaAllocationMasterData, updateParam)
}

/**
 * トラクターヘッドマスタを取得する。
 *
 * @async
 * @returns {Promise<MasterData>} トラクターヘッド
 */
export async function getTractorHeadMasterData(): Promise<MasterData> {
  const res = await apiClient.get(config.api.getTractorHeadMasterData)
  return res.data.data as MasterData
}

/**
 * トラクターヘッドマスタを更新する。
 *
 * @async
 * @param {MasterUpdateParam} tractorHeadUpdateParam トラクターヘッドマスタ更新のパラメータ
 * @returns {Promise<void>}
 */
export async function updateTractorHeadMasterData(tractorHeadUpdateParam: MasterUpdateParam): Promise<void> {
  await apiClient.post(config.api.updateTractorHeadMasterData, tractorHeadUpdateParam)
}

/**
 * トラクターヘッドCSVダウンロード
 *
 * @returns {Promise<FileDownloadResponse>} ダウンロードファイル
 */
export async function tractorHeadMasterCSVDownload(): Promise<FileDownloadResponse> {
  const res = await apiClient.get(config.api.tractorHeadMasterDownload, { responseType: 'blob' })

  return res as unknown as FileDownloadResponse
}

/**
 * エリアマスタを取得する。
 *
 * @async
 * @returns {Promise<MasterData>} エリアマスタ
 */
export async function getAreaMasterData(): Promise<MasterData> {
  const res = await apiClient.get(config.api.getAreaMasterData)

  return res.data.data as MasterData
}

/**
 * エリアマスタを更新する。
 *
 * @async
 * @param {MasterUpdateParam} areaMasterUpdateParam エリアマスタマスタ更新のパラメータ
 * @returns {Promise<void>} エリアマスタ
 */
export async function updateAreaMasterData(areaMasterUpdateParam: MasterUpdateParam): Promise<void> {
  await apiClient.post(config.api.updateAreaMasterData, areaMasterUpdateParam)
}

/**
 * GPSトラッカーマスタを取得する。
 *
 * @async
 * @param {MasterGetParam} reqParams パラメータ
 * @returns {Promise<MasterData>} GPSトラッカーマスタ
 */
export async function getGPSTrackerMasterData(reqParams: MasterGetParam): Promise<MasterData> {
  const res = await apiClient.get(config.api.getGpsTrackerMasterData, {
    params: reqParams
  })

  return res.data.data as MasterData
}

/**
 * GPSトラッカーマスタを更新する。
 *
 * @async
 * @param {MasterUpdateParam} gpsTrackerUpdateParam GPSトラッカーマスタ更新のパラメータ
 * @returns {Promise<void>}
 */
export async function updateGPSTrackerMasterData(gpsTrackerUpdateParam: MasterUpdateParam): Promise<void> {
  await apiClient.post(config.api.updateGpsTrackerMasterData, gpsTrackerUpdateParam)
}

/**
 * GPSトラッカーマスタCSVダウンロード
 *
 * @param {MasterGetParam} reqParams パラメータ
 * @returns {Promise<FileDownloadResponse>} ダウンロードファイル
 */
export async function gpsTrackerMasterCSVDownload(reqParams: MasterGetParam): Promise<FileDownloadResponse> {
  const res = await apiClient.get(config.api.gpsTrackerMasterDownload, {
    params: reqParams,
    responseType: 'blob'
  })

  return res as unknown as FileDownloadResponse
}

/**
 * クラウドアプリ制御設定値一覧取得
 *
 * @async
 * @param {MasterGetParam} reqParams パラメータ
 * @returns {Promise<MasterData>} クラウドアプリ制御設定値一覧
 */
export async function getDeviceCloudSettingList(reqParams: MasterGetParam): Promise<MasterData> {
  const res = await apiClient.post(config.api.getDeviceCloudSettingList, reqParams)

  return res.data.data as MasterData
}

/**
 * 受信回数カウント日別CSVダウンロード
 *
 * @param {TrustedRepliesCount} trustedRepliesCountParam 受信回数カウント日別CSVダウンロードのパラメータ
 * @returns {Promise<FileDownloadResponse>} ダウンロードファイル
 */
export async function gpsTrackerDateCounCSVDownload(
  trustedRepliesCountParam: TrustedRepliesCount
): Promise<FileDownloadResponse> {
  const res = await apiClient.post(config.api.gpsTrackerDateCounCSVDownload, trustedRepliesCountParam, {
    responseType: 'blob'
  })

  return res as unknown as FileDownloadResponse
}

/**
 * 受信回数カウント月別CSVダウンロード
 *
 * @param {TrustedRepliesCount} trustedRepliesCountParam 受信回数カウント月別CSVダウンロードのパラメータ
 * @returns {Promise<FileDownloadResponse>} ダウンロードファイル
 */
export async function gpsTrackerMonthCounCSVDownload(
  trustedRepliesCountParam: TrustedRepliesCount
): Promise<FileDownloadResponse> {
  const res = await apiClient.post(config.api.gpsTrackerMonthCounCSVDownload, trustedRepliesCountParam, {
    responseType: 'blob'
  })

  return res as unknown as FileDownloadResponse
}

/**
 * 管理場所を取得する。
 *
 * @async
 * @param {MasterGetParam} reqParams パラメータ
 * @returns {Promise<MasterData>} 管理場所
 */
export async function getManagementPlaceMasterData(reqParams: MasterGetParam): Promise<MasterData> {
  const res = await apiClient.post(config.api.getManagementPlaceMasterData, reqParams)

  return res.data.data as MasterData
}

/**
 * 管理場所を更新する。
 *
 * @async
 * @param {MasterUpdateParam} updateParam 管理場所更新のパラメータ
 * @returns {Promise<void>}
 */
export async function updateManagementPlaceMasterData(updateParam: MasterUpdateParam): Promise<void> {
  await apiClient.post(config.api.updateManagementPlaceMasterData, updateParam)
}

/**
 * 認証済み画像URL取得
 *
 * @async
 * @param {ImageGettingParam} reqParams 認証済み画像URLのパラメータ
 * @returns {Promise<ImageGettingParam>} 画像パス
 */
export async function getPlaceImage(reqParams: ImageGettingParam): Promise<ImageGettingParam> {
  const res = await apiClient.get(config.api.getPlaceImage, {
    params: reqParams
  })

  return res.data.data as ImageGettingParam
}

/**
 * 管理場所CSVダウンロード
 *
 * @param {MasterGetParam} reqParams パラメータ
 * @returns {Promise<FileDownloadResponse>} ダウンロードファイル
 */
export async function managementPlaceCSVDownload(reqParams: MasterGetParam): Promise<FileDownloadResponse> {
  const res = await apiClient.get(config.api.managementPlaceCSVDownload, {
    params: reqParams,
    responseType: 'blob'
  })

  return res as unknown as FileDownloadResponse
}

/**
 * 管理場所詳細を取得する。
 *
 * @async
 * @param {MasterGetParam} reqParams パラメータ
 * @returns {Promise<MasterData>} 管理場所詳細
 */
export async function getManagementPlaceDetailMasterData(reqParams: MasterGetParam): Promise<MasterData> {
  const res = await apiClient.get(config.api.getManagementPlaceDetailMasterData, {
    params: reqParams
  })

  return res.data.data as MasterData
}

/**
 * 管理場所詳細を更新する。
 *
 * @async
 * @param {MasterUpdateParam} updateParam 管理場所更新のパラメータ
 * @returns {Promise<void>}
 */
export async function updateManagementPlaceDetailMasterData(updateParam: MasterUpdateParam): Promise<void> {
  await apiClient.post(config.api.updateManagementPlaceDetailMasterData, updateParam)
}

/**
 * 管理場所詳細CSVダウンロード
 *
 * @param {MasterGetParam} reqParams パラメータ
 * @returns {Promise<FileDownloadResponse>} ダウンロードファイル
 */
export async function managementPlaceDetailCSVDownload(reqParams: MasterGetParam): Promise<FileDownloadResponse> {
  const res = await apiClient.get(config.api.managementPlaceDetailCSVDownload, {
    params: reqParams,
    responseType: 'blob'
  })

  return res as unknown as FileDownloadResponse
}

/**
 * 管理対象マスタを取得する。
 *
 * @param {MasterGetParam} reqParams パラメータ
 * @returns {Promise<MasterData>} 管理対象マスタ
 */
export async function getManagementTargetMasterData(reqParams: MasterGetParam): Promise<MasterData> {
  const res = await apiClient.get(config.api.getManagementTargetMasterData, {
    params: reqParams
  })

  return res.data.data as MasterData
}

/**
 * 管理対象を更新する。
 *
 * @async
 * @param {MasterUpdateParam} updateParam 管理対象更新のパラメータ
 * @returns {Promise<void>}
 */
export async function updateManagementTargetMasterData(updateParam: MasterUpdateParam): Promise<void> {
  await apiClient.post(config.api.updateManagementTargetMasterData, updateParam)
}

/**
 * 管理対象CSVダウンロード
 *
 * @param {MasterGetParam} reqParams パラメータ
 * @returns {Promise<FileDownloadResponse>} ダウンロードファイル
 */
export async function managementTargetCSVDownload(reqParams: MasterGetParam): Promise<FileDownloadResponse> {
  const res = await apiClient.get(config.api.managementTargetCSVDownload, {
    params: reqParams,
    responseType: 'blob'
  })

  return res as unknown as FileDownloadResponse
}

/**
 * 管理対象割当を取得する。
 *
 * @async
 * @returns {Promise<ManagementTargetAllocData>} 管理対象割当
 */
export async function getManagementTargetAllocationMasterData(): Promise<ManagementTargetAllocData> {
  const res = await apiClient.get(config.api.getManagementTargetAllocMasterData)

  return res.data.data as ManagementTargetAllocData
}

/**
 * 管理対象割当保存
 *
 * @async
 * @param {MasterUpdateParam} updateParam  管理対象割当保存パラメータ
 * @returns {Promise<void>}
 */
export async function updateManagementTargetAllocationMasterData(updateParam: MasterUpdateParam): Promise<void> {
  await apiClient.post(config.api.updateManagementTargetAllocMasterData, updateParam)
}

/**
 * 連結可能トラクターヘッド一覧取得
 *
 * @param {MasterGetParam} reqParams パラメータ
 * @returns {Promise<MasterData>} 連結可能トラクターヘッド一覧
 */
export async function getAvlTractorHeadMasterData(reqParams: MasterGetParam): Promise<MasterData> {
  const res = await apiClient.get(config.api.getAvlTractorHeadMasterData, {
    params: reqParams
  })

  return res.data.data as MasterData
}

/**
 * 連結可能トラクターヘッドを更新する。
 *
 * @async
 * @param {MasterUpdateParam} updateParam 管理対象更新のパラメータ
 * @returns {Promise<void>}
 */
export async function updateAvlTractorHeadMasterData(updateParam: MasterUpdateParam): Promise<void> {
  await apiClient.post(config.api.updateAvlTractorHeadMasterData, updateParam)
}

/**
 * 連結可能トラクターヘッドCSVダウンロード
 *
 * @param {MasterGetParam} reqParams パラメータ
 * @returns {Promise<FileDownloadResponse>} ダウンロードファイル
 */
export async function avlTractorHeadCSVDownload(reqParams: MasterGetParam): Promise<FileDownloadResponse> {
  const res = await apiClient.get(config.api.avlTractorHeadCSVDownload, {
    params: reqParams,
    responseType: 'blob'
  })

  return res as unknown as FileDownloadResponse
}

/**
 * GPSトラッカー端末判定値設定を取得する。
 *
 * @param {MasterGetParam} reqParams パラメータ
 * @returns {Promise<GpsTrackerTerminalConfigData>} GPSトラッカー端末判定値設定
 */
export async function getGpsTrackerTerminalConfigData(
  reqParams: MasterGetParam
): Promise<GpsTrackerTerminalConfigData> {
  const res = await apiClient.get(config.api.getGpsTrackerTerminalConfigData, {
    params: reqParams
  })

  return res.data.data as GpsTrackerTerminalConfigData
}

/**
 *  GPSトラッカー端末判定値設定を保存する。
 *
 * @async
 * @param {GpsTrackerTerminalConfigData} params  GPSトラッカー端末判定値設定パラメータ
 * @returns {Promise<void>}
 */
export async function updateGpsTrackerTerminalConfig(params: GpsTrackerTerminalConfigData): Promise<void> {
  const res = await apiClient.post(config.api.updateGpsTrackerTerminalConfigData, params)
  if (!res || !res.data) {
    throw new Error('apis > lock: res is null')
  }
}

/**
 * 営業所割当を取得する。
 *
 * @async
 * @returns {Promise<OfficeMasterData>} 営業所マスタ
 */
export async function getSalesOfficeAllocationMasterData(): Promise<OfficeMasterData> {
  const res = await apiClient.get(config.api.getSalesOfficeAllocationMasterData)

  return res.data.data as OfficeMasterData
}

/**
 * 営業所割当を取得する。
 *
 * @async
 * @returns {Promise<OfficeMasterData>} 営業所マスタ
 */
export async function getSalesOfficeAllocationData(): Promise<OfficeMasterData> {
  const res = await apiClient.get(config.api.getSalesOfficeAllocationData)

  return res.data.data as OfficeMasterData
}

/**
 * 営業所割当保存
 *
 * @async
 * @param {MasterUpdateParam} updateParam  営業所割当保存パラメータ
 * @returns {Promise<void>}
 */
export async function updateSalesOfficeAllocationMasterData(updateParam: MasterUpdateParam): Promise<void> {
  await apiClient.post(config.api.updateSalesOfficeAllocationMasterData, updateParam)
}

/**
 * バッテリー交換を保存する。
 *
 * @async
 * @param {TrackerExchangeForm} params バッテリー交換情報パラメータ
 * @returns {Promise<void>}
 */
export async function updateTrackerExchangeInfo(params: TrackerExchangeForm): Promise<void> {
  await apiClient.post(config.api.trackerExchange, params)
}

/**
 * GPSトラッカー詳細取得。
 *
 * @async
 * @param {TrackerDetailParam} params バッテリー交換情報パラメータ
 * @returns {Promise<TrackerDetail>} GPSトラッカー詳細
 */
export async function getTrackerDetail(params: TrackerDetailParam): Promise<TrackerDetail> {
  const res = await apiClient.get(config.api.getTrackerDetail, { params: params })

  return res.data.data as TrackerDetail
}

/**
 * デジタコ連携会社マスタを取得する。
 *
 * @async
 * @returns {Promise<MasterData>} デジタコ連携会社マスタ
 */
export async function getConnectCompanyMasterData(): Promise<MasterData> {
  const res = await apiClient.get(config.api.getConnectCompanyMasterData)
  return res.data.data as MasterData
}

/**
 * デジタコ連携会社マスタを更新する。
 *
 * @async
 * @param {MasterUpdateParam} connectCompanyUpdateParam デジタコ連携会社マスタ更新のパラメータ
 * @returns {Promise<void>}
 */
export async function updateConnectCompanyMasterData(connectCompanyUpdateParam: MasterUpdateParam): Promise<void> {
  await apiClient.post(config.api.updateConnectCompanyMasterData, connectCompanyUpdateParam)
}

/**
 * デジタコ連携会社CSVダウンロード
 *
 * @returns {Promise<FileDownloadResponse>} ダウンロードファイル
 */
export async function ConnectCompanyMasterCSVDownload(): Promise<FileDownloadResponse> {
  const res = await apiClient.get(config.api.connectCompanyMasterCsvDownload, { responseType: 'blob' })

  return res as unknown as FileDownloadResponse
}
